import React, { Fragment, useEffect, useRef, useState } from "react";
import CommonCard from "../../common/components/CommonCard";
import { Box, Divider, Grid, IconButton, Typography } from "@mui/material";
import theme from "../../theme";
import CommonButton from "../../common/components/CommonButton";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import CommonDialog from "../../common/components/CommonDialog";
import { Close, Description } from "@mui/icons-material";
import CommonFormField from "../../common/components/CommonFormField";
import { createNotes, getPastSession } from "../../api/sessionApi";
import { serviceOptions } from "../../common/const";
import Dropzone from "react-dropzone";
import NoteViewer from "../../common/components/NoteViewer";

const SessionHistory = ({
    currentService,
    studentId,
    subService,
    activeTab
}) => {
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [events, setEvents] = useState([]);
    const [selectedSession, setSelectedSesion] = useState(null);
    const [addNotes, setAddNotes] = useState(false);
    const [viewNotes, setViewNotes] = useState(false);
    const [page, setPage] = useState(1);
    const scrollRef = useRef(null);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [fileNames, setFileNames] = useState([]);
    const [formData, setFormData] = useState({
        note: '',
        notes: '',
        recordedLinks: '',
    });
    const rowsPerPage = 10;

    const getEvents = async () => {
        try {
            if (page > 1) {
                setIsFetchingMore(true);
            } else {
                dispatch(setLoading({ loading: true }));
            }
            const response = await getPastSession(page, rowsPerPage, currentService, studentId, subService);
            if (response?.data?.data.length === 0) {
                if(page === 1) {
                    setEvents([]);
                }
                setHasMore(false);
            } else {
                setEvents(prev =>
                    page === 1 ? response?.data?.data : [...prev, ...response?.data?.data]
                );
            }
            dispatch(setLoading({
                loading: false
            }));
            setIsFetchingMore(false);
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
            setIsFetchingMore(false);
        }
    }

    useEffect(() => {
        if (user?.user_id) {
            getEvents();
        }
    }, [user?.user_id, page, subService, activeTab]);

    useEffect(() => {
        setPage(1);
    }, [activeTab, subService]);

    const handleInputChange = (e) => {
        const { name, value, files } = e.target;
        if (files && files.length > 0) {
            const file = files;
            setFormData((prev) => ({
                ...prev,
                [name]: file,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <CommonDialog
                open={addNotes || viewNotes}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600, fontSize: 18 }}>{viewNotes ? 'View Summary' : 'Add Notes'}</Typography>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setAddNotes(false);
                                    setViewNotes(false);
                                    setSelectedSesion(null);
                                    setFileNames([]);
                                    setFormData({
                                        note: '',
                                        notes: '',
                                        recordedLinks: '',
                                    });
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    viewNotes ? (
                        selectedSession?.sessionNotes.map((a) => (
                            <NoteViewer note={a} />
                        ))
                    ) : (
                        <Grid container spacing={2} mt={0.5} mb={1}>
                            <Grid item md={12} sm={12}>
                                <CommonFormField
                                    type="email"
                                    name="note"
                                    value={formData.note}
                                    onChange={handleInputChange}
                                    placeholder="Enter note"
                                    multiline
                                />
                            </Grid>
                            <Grid item md={12} sm={12}>
                                <CommonFormField
                                    type="file"
                                    name="notes"
                                    value={formData.notes?.[0]?.name}
                                    onChange={handleInputChange}
                                    placeholder="Add Notes"
                                />
                            </Grid>
                            <Grid item md={12} sm={12}>
                                <CommonFormField
                                    type="email"
                                    name="recordedLinks"
                                    value={formData.recordedLinks}
                                    onChange={handleInputChange}
                                    placeholder="Add Recording Link"
                                />
                            </Grid>
                            <Grid item md={12} sm={12}>
                                <Box sx={{ width: '100%' }}>
                                    {fileNames.length > 0 ? (
                                        // Show selected files
                                        <Box sx={{ mt: 2 }}>
                                            {fileNames.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        p: 2,
                                                        border: '1px solid',
                                                        borderColor: 'grey.300',
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        backgroundColor: 'background.paper'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <Description color="primary" />
                                                        <Box>
                                                            <Typography variant="body1">{file.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {(file.size / 1024).toFixed(2)} KB
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setFileNames(fileNames.splice(index, -1))}
                                                        color="error"
                                                    >
                                                        <Close />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        // Show dropzone when no files
                                        <Dropzone
                                            onDrop={handleDrop}
                                        >
                                            {({ getRootProps, getInputProps, isDragActive }) => (
                                                <Box
                                                    {...getRootProps()}
                                                    sx={{
                                                        border: '2px dashed',
                                                        borderColor: '#D8DAEB',
                                                        borderRadius: 2,
                                                        padding: { xs: 3, md: 6 },
                                                        textAlign: 'center',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease',
                                                        minHeight: 100,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <input {...getInputProps()} />
                                                    <img src="/Images/sound.png" alt="upload" />
                                                    <Typography fontWeight={600} gutterBottom>
                                                        Drag and drop your files
                                                    </Typography>
                                                    <CommonButton
                                                        label="Select File"
                                                        color="Black"
                                                        background="White"
                                                        border={`1px solid ${theme.palette.background.secondary}`}
                                                        width="100px"
                                                        height="40px"
                                                        marginBottom="0px"
                                                        marginTop="10px"
                                                    />
                                                </Box>
                                            )}
                                        </Dropzone>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    )
                }
                actionButtons={!viewNotes && (
                    <CommonButton
                        label={viewNotes ? "Close" : "Save"}
                        size="small"
                        background={theme.palette.secondary.main}
                        color={theme.palette.background.main}
                        fontWeight={700}
                        marginBottom="10px"
                        handleClick={async () => {
                            if (viewNotes) {
                                setViewNotes(false);
                            } else {
                                try {
                                    if (formData?.notes?.length > 0 || formData.note !== '' || formData.recordedLinks !== '' || fileNames?.length > 0) {
                                        dispatch(setLoading({
                                            loading: true
                                        }));
                                        const data = new FormData();
                                        if (formData.note !== '') {
                                            data.append('description', formData.note);
                                        }
                                        if (formData?.notes?.length > 0) {
                                            data.append('attachment', formData.notes[0]);
                                        }
                                        if (formData.recordedLinks !== '') {
                                            data.append('recording_link', formData.recordedLinks);
                                        }
                                        fileNames.map((a) => {
                                            data.append('other_doc', a);
                                        })
                                        data.append('mentor_profile_id', selectedSession?.mentor_profile_id);
                                        data.append('mentor_session_id', selectedSession?.mentor_session_id);
                                        data.append('student_id', studentId);
                                        const response = await createNotes(data);
                                        if (response.success) {
                                            showNotification(response.message, 'success');
                                            setAddNotes(false);
                                            setSelectedSesion(null);
                                            setFileNames([]);
                                            setFormData({
                                                note: '',
                                                notes: '',
                                                recordedLinks: '',
                                            });
                                            dispatch(setLoading({
                                                loading: false
                                            }));
                                            setPage(1);
                                            getEvents();
                                        }
                                    } else {
                                        showNotification('Please provide any detail for add note', 'error');
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }
                        }}
                    />
                )}
            />
            <CommonCard
                borderRadius={2}
                headerContent={
                    <Fragment>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: '13px' }}
                        >
                            <Box>
                                <Typography fontWeight={600}>Session History</Typography>
                                <Typography fontSize={14} color={theme.palette.background.dark}>Past sessions and meeting summaries</Typography>
                            </Box>
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Box
                        ref={scrollRef}
                        onScroll={(e) => {
                            const { scrollTop, scrollHeight, clientHeight } = e.target;

                            const isNearBottom =
                                scrollHeight - scrollTop <= clientHeight + 50;

                            if (isNearBottom && !isFetchingMore && hasMore) {
                                setIsFetchingMore(true);   // 🔒 lock
                                setPage(prev => prev + 1);
                            }
                        }}
                        sx={{
                            height: events.length > 0 ? '40vh' : '4vh',
                            overflow: 'auto'
                        }}
                    >
                        {events.length > 0 ? events.map((a, index) => {
                            return (
                                <CommonCard
                                    key={index}
                                    background={theme.palette.background.main}
                                    borderRadius={2}
                                    paddingBottom="0px"
                                    padding="13px"
                                    marginTop={index === 0 ? "0px" : "10px"}
                                    content={
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                gap={1}
                                                alignItems="center"
                                            >
                                                <Box
                                                    sx={{
                                                        background: theme.palette.secondary.main,
                                                        borderRadius: 1,
                                                        padding: 1,
                                                        width: '65px',
                                                        cursor: 'pointer',
                                                        border: `1px solid ${theme.palette.background.secondary}`
                                                    }}
                                                >
                                                    <Typography fontSize={19} fontWeight={600} color={theme.palette.primary.main} textAlign="center">{moment(a.session_start_time).date()}</Typography>
                                                    <Typography fontSize={11} fontWeight={600} color="White" textAlign="center">{moment(a.session_start_time).format('MMM YYYY')}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography
                                                        fontSize={{ xs: 13, sm: 14 }}
                                                        fontWeight="600"
                                                        sx={{
                                                            wordBreak: 'break-word'
                                                        }}
                                                    >
                                                        {a.topic || serviceOptions.find((i) => i.value === a.service_name)?.label}
                                                    </Typography>
                                                    <Typography fontSize={13} color={theme.palette.background.dark}>
                                                        {`${moment(a.session_start_time).format('hh:mm A')} - ${moment(a.session_end_time).format('hh:mm A')}`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {a.sessionNotes?.length === 0 ? (
                                                <CommonButton
                                                    marginBottom="0px"
                                                    label="Add Notes"
                                                    width="120px"
                                                    fontSize="12px"
                                                    height="30px"
                                                    fontWeight="600"
                                                    color={theme.palette.background.dark}
                                                    background="Transparent"
                                                    border={`1px solid #0000001A`}
                                                    handleClick={() => {
                                                        setAddNotes(true);
                                                        setSelectedSesion(a);
                                                    }}
                                                />
                                            ) : (
                                                <CommonButton
                                                    marginBottom="0px"
                                                    label="View Notes"
                                                    width="120px"
                                                    fontSize="12px"
                                                    height="30px"
                                                    fontWeight="600"
                                                    color={theme.palette.background.dark}
                                                    background="Transparent"
                                                    border={`1px solid #0000001A`}
                                                    handleClick={() => {
                                                        setViewNotes(true);
                                                        setSelectedSesion(a);
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                            )
                        }) : (
                            <Typography textAlign="center" color={theme.palette.background.dark}>No Session Found</Typography>
                        )}
                    </Box>
                }
                paddingBottom="0px"
                padding="13px"
                marginTop="15px"
            />
        </Fragment>
    )
}

export default SessionHistory;